import { Card, CardActions, CardContent, CardMedia, Grid2, Stack, Typography, useTheme } from '@mui/material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { getAdditionalProductsApi } from 'api/packages/query';
import { NumberInputActionField } from 'components/v2/input/NumberInputActionField';
import Modal from 'components/v2/modal/Modal';
import TextTooltip from 'components/v2/text/TextTooltip';
import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';
import { LAYOUT_SPACING, MODAL_WIDTH } from 'constants/ui/style';
import { useLocalization } from 'localization';
import { userStore } from 'stores/userStore';
import { addOrderLineSchema } from 'components/v2/division/form/schema/orderSchema';
import { HiddenInputField } from 'components/v2/input/HiddenInputField';
import { addOrderLinesMutation } from 'api/orders/query';
import SkeletonContainer from 'components/v2/container/SkeletonContainer';
import { DEFAULT_IMG_SOURCE } from 'modules/material/MaterialItem';

const AddOrderLineModal = () => {
  const { t } = useLocalization();
  const { user } = userStore();
  const { palette } = useTheme();

  const { toggleModal, isModalOpen, getModalData } = MODALS_STORE.orders();

  const branchId = user?.branches.find(
    ({ axCustomerId }) => axCustomerId === getModalData(MODAL_ID.ADD_ORDER_LINE)?.clientId
  )?.id;

  const { data, isLoading } = getAdditionalProductsApi({
    branchId: branchId,
    enabled: !!branchId,
  });

  const defaultFormValue = data?.map(product => ({
    itemId: product.itemNumber,
    quantity: 0,
    variant: product.variant,
    config: product.config,
  }));

  const { control, watch, getValues } = useForm({
    resolver: yupResolver(addOrderLineSchema()),
    defaultValues: { product: defaultFormValue },
    mode: 'onChange',
    values: { product: defaultFormValue },
  });

  const { mutateAsync, isLoading: isAddingOrderLine } = addOrderLinesMutation({
    orderId: getModalData(MODAL_ID.ADD_ORDER_LINE)?.orderId,
    addOrderLinePayload: getValues('product')?.filter(({ quantity }) => quantity > 0),
    handleSuccess: () => toggleModal(MODAL_ID.ADD_ORDER_LINE),
  });

  const isOrderLineAdded = watch('product')?.some(item => item.quantity > 0);

  return (
    <Modal
      modalTitle={t('common.terms.addExtraOrderLine')}
      isOpen={isModalOpen(MODAL_ID.ADD_ORDER_LINE)}
      toggleModal={() => toggleModal(MODAL_ID.ADD_ORDER_LINE)}
      onConfirm={mutateAsync}
      width={MODAL_WIDTH.xxxl}
      disableConfirm={!isOrderLineAdded}
      isLoading={isAddingOrderLine}
    >
      <Stack sx={{ minHeight: '80vh' }} spacing={LAYOUT_SPACING.CARD.md}>
        <Typography variant="h5">{t('common.terms.purchaseIndividualProductsTitle')}: </Typography>
        <SkeletonContainer
          loading={isLoading}
          rows={{ xs: 1, sm: 2 }}
          columns={{ xs: 1, md: 2, lg: 4 }}
          skeletonHeight={350}
        >
          <Grid2 container spacing={2}>
            {data?.map((product, index) => {
              const isQuantityUpdated = (watch(`product.${index}.quantity`) ?? 0) > 0;
              return (
                <Grid2 key={product.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Card
                    sx={{
                      borderColor: isQuantityUpdated ? palette.primary.main : 'none',
                      padding: 1,
                    }}
                  >
                    <CardMedia
                      onError={e => (e.currentTarget.src = DEFAULT_IMG_SOURCE)}
                      sx={{ height: 120 }}
                      component="img"
                      image={product.thumbnail}
                    />
                    <CardContent sx={{ height: 130, padding: 0 }}>
                      <TextTooltip
                        value={product.name}
                        textProps={{ fontWeight: 'bold', margin: '10px 0' }}
                        maxLength={50}
                      />
                      <TextTooltip value={product.description ?? ''} maxLength={70} />
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', padding: 0 }}>
                      <p>
                        {product.price} {product.currency}
                      </p>
                      <NumberInputActionField control={control} name={`product.${index}.quantity`} min={0} />
                      <HiddenInputField control={control} name={`product.${index}.itemId`} />
                    </CardActions>
                  </Card>
                </Grid2>
              );
            })}
          </Grid2>
        </SkeletonContainer>
      </Stack>
    </Modal>
  );
};

export default AddOrderLineModal;
